import { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  useAffiliation,
  useAffiliations,
  useAuth,
  useCategories,
  useCategory,
  useDisableTourMutation,
  useTenant,
  useUser
} from '../../hooks'
import { useDisabled } from '../../store'
import { PERMISSION } from '../../helpers'
import steps from './steps.json'

const { env } = window

const REQUEST_ACCESS = { id: 1, name: 'Request Access' }
const MANAGE_ACCOUNT = { id: 2, name: 'Manage Account' }
const MY_PROJECTS = { id: 3, name: 'My Projects' }
const PROJECT_SETTINGS = { id: 4, name: 'Project Settings' }
const PROJECT_USERS = { id: 5, name: 'Project User Management' }
const LOGOUT = { id: 6, name: 'Logout' }

export const useLayoutViewModel = () => {
  const { tenantUrlSlug, affiliationUrlSlug } = useParams()
  const { logIn, logOut, loggedIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const { user } = useUser()
  const { tenant } = useTenant()
  const { affiliations } = useAffiliations()
  const { affiliation } = useAffiliation()
  const { categories } = useCategories()
  const { category } = useCategory()
  const disabled = useDisabled()

  const { mutate: disableTour } = useDisableTourMutation()

  const [runTour, setRunTour] = useState(false)

  useEffect(() => document.body.focus(), [location.pathname])
  useEffect(() => setRunTour(Boolean(user?.enableTour)), [user?.enableTour])

  const options = affiliations?.map((item) => {
    let name = item.name
    if (item.permissions?.includes(PERMISSION.ADMIN)) {
      name += ' (admin)'
    } else if (item.permissions?.includes(PERMISSION.EDIT)) {
      name += ' (editor)'
    }
    return { ...item, name }
  })

  const handleOptionSelect = (option) => {
    if (option.urlSlug !== affiliationUrlSlug) {
      navigate(`/${tenantUrlSlug}/${option.urlSlug}`)
    }
  }

  const links = categories?.map((c) => {
    const href = `/${tenantUrlSlug}/${affiliationUrlSlug}/${c.urlSlug}`
    return {
      url: href,
      name: c.name,
      props: { as: NavLink, to: href, active: false }
    }
  })

  const userOptions = []
  tenant && userOptions.push(REQUEST_ACCESS)
  userOptions.push(...[MANAGE_ACCOUNT, MY_PROJECTS])
  if (tenant?.roles?.includes('ADMIN')) {
    userOptions.push(PROJECT_SETTINGS)
    userOptions.push(PROJECT_USERS)
  }
  userOptions.push(LOGOUT)

  const handleUserOptionSelect = (option) => {
    switch (option) {
      case REQUEST_ACCESS:
        navigate(`/${tenantUrlSlug}/${affiliationUrlSlug}/request-access`)
        break
      case MANAGE_ACCOUNT:
        window.open(env.accountUrl, '_blank', 'noopener,noreferrer')
        break
      case MY_PROJECTS:
        navigate('/myprojects')
        break
      case PROJECT_SETTINGS:
        navigate(`/${tenantUrlSlug}/${affiliationUrlSlug}/project-admin`)
        break
      case PROJECT_USERS:
        navigate(`/${tenantUrlSlug}/${affiliationUrlSlug}/project-users`)
        break
      case LOGOUT:
        logOut()
        navigate('/')
        break
      default:
        alert(`${option.name} is not handled`)
    }
  }

  const helpOptions = env.helpUrl ? [{ id: 1, name: 'Help Center' }] : []
  steps?.length && helpOptions.push({ id: 2, name: 'Show Page Tooltips' })
  env.contactUrl && helpOptions.push({ id: 3, name: 'Contact Us' })

  const handleHelpOptionSelect = (option) => {
    switch (option.name) {
      case 'Help Center':
        window.open(env.helpUrl, '_blank', 'noopener,noreferrer')
        break
      case 'Show Page Tooltips':
        setRunTour(true)
        break
      case 'Contact Us':
        window.open(env.contactUrl, '_blank', 'noopener,noreferrer')
        break
      default:
        alert(`${option.name} is not handled`)
    }
  }

  const onTourClose = () => {
    user?.enableTour && disableTour()
    setRunTour(false)
  }

  return {
    disabled,
    options,
    selectedOption: affiliation,
    handleOptionSelect,
    links,
    loggedIn,
    handleLogin: logIn,
    user,
    userOptions,
    handleUserOptionSelect,
    helpOptions,
    handleHelpOptionSelect,
    category,
    isGlobal: categories && categories[0] === category,
    steps,
    runTour,
    onTourClose
  }
}
